import React from 'react';
import { Box, Button, Card, Typography, Stack, LinearProgress } from '@mui/joy';
import { ArrowLeft as ArrowBack, ArrowRight as ArrowForward, SkipForward as SkipNext, X as Close } from '@phosphor-icons/react';
import { SetupVideoPlaceholder } from './SetupVideoPlaceholder';
import { SetupProgressIndicator } from './SetupProgressIndicator';
import {
  SETUP_STEPS,
  getStepIndex,
  getNextStep,
  getPreviousStep,
} from '../../types/setup';
import type { SetupStep, SetupProgress, SetupStepId } from '../../types/setup';

interface SetupStepLayoutProps {
  step: SetupStep;
  progress: SetupProgress;
  children: React.ReactNode;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onExit: () => void;
  onStepClick: (stepId: SetupStepId) => void;
  isLoading?: boolean;
  nextLabel?: string;
  showSkip?: boolean;
}

export const SetupStepLayout: React.FC<SetupStepLayoutProps> = ({
  step,
  progress,
  children,
  onNext,
  onBack,
  onSkip,
  onExit,
  onStepClick,
  isLoading = false,
  nextLabel = 'Save & Continue',
  showSkip = true,
}) => {
  const currentIndex = getStepIndex(step.id);
  const totalSteps = SETUP_STEPS.length;
  const hasPrevious = !!getPreviousStep(step.id);
  const hasNext = !!getNextStep(step.id);
  const isLastStep = currentIndex === totalSteps - 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.level1',
        py: 3,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header with exit button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography level="h4">Quick Setup</Typography>
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<Close />}
            onClick={onExit}
          >
            Exit to Dashboard
          </Button>
        </Box>

        {/* Progress indicator */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                Step {currentIndex + 1} of {totalSteps}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                {progress.progress_percentage}% complete
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progress.progress_percentage}
              sx={{
                '--LinearProgress-thickness': '6px',
                '--LinearProgress-progressRadius': '4px',
              }}
            />
          </Box>
          <SetupProgressIndicator
            currentStep={step.id}
            progress={progress}
            onStepClick={onStepClick}
          />
        </Card>

        {/* Main content card */}
        <Card variant="outlined" sx={{ p: 3 }}>
          {/* Video placeholder */}
          <SetupVideoPlaceholder videoUrl={step.videoUrl} />

          {/* Step title and description */}
          <Typography level="h3" sx={{ mb: 1 }}>
            {step.title}
          </Typography>
          <Typography level="body-md" sx={{ color: 'neutral.600', mb: 3 }}>
            {step.description}
          </Typography>

          {/* Step content */}
          <Box sx={{ mb: 4 }}>{children}</Box>

          {/* Navigation buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBack />}
              onClick={onBack}
              disabled={!hasPrevious || isLoading}
            >
              Back
            </Button>

            <Stack direction="row" spacing={1}>
              {showSkip && !isLastStep && (
                <Button
                  variant="plain"
                  color="neutral"
                  endDecorator={<SkipNext />}
                  onClick={onSkip}
                  disabled={isLoading}
                >
                  Skip
                </Button>
              )}
              <Button
                variant="solid"
                color="primary"
                endDecorator={!isLastStep && <ArrowForward />}
                onClick={onNext}
                loading={isLoading}
              >
                {isLastStep ? 'Finish Setup' : nextLabel}
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};

export default SetupStepLayout;
